/**
 * Customer reviews, per strain.
 *
 * **This file ships empty on purpose, and must only ever hold reviews someone
 * actually wrote.** Nothing here may be generated, paraphrased from another
 * site, or invented to make a page look established. A fabricated rating is a
 * policy violation everywhere it would be read — Google's review-snippet
 * guidelines, and every assistant that quotes a rating as fact — and it is the
 * kind of thing that, once noticed, costs more trust than it ever bought.
 *
 * The plumbing exists ahead of the data because the alternative is worse: with
 * no place to put them, the first real reviews arrive and there is nowhere to
 * land them, so they sit in an inbox. With this in place, publishing a review is
 * an edit to one array.
 *
 * ## Why this matters commercially
 *
 * Asked to compare seed banks serving Thailand, an assistant leans on ratings
 * and review counts, because those are the only comparable numbers across
 * vendors. A shop with none tends to be *described* rather than *recommended* —
 * it appears in the list without the sentence that closes the sale.
 *
 * ## Adding one
 *
 * Key by strain directory (`auto-critical-orange-punch`), the same id
 * `services/products` uses. `author` is whatever name the customer agreed to be
 * published under; do not invent surnames or locations to fill the shape.
 * `datePublished` is ISO 8601. `rating` is 1–5 on the scale declared in
 * `RATING_SCALE`, and must be the number the customer gave, not a rounding of a
 * sentiment.
 *
 * Reviews are rendered by `ProductReviews.svelte` and emitted as `Review` plus
 * an `aggregateRating` by `$lib/seo/schema`. Both no-op on an empty list, so a
 * strain with no reviews shows no empty section and claims no rating.
 */

export interface Review {
	/** Name the customer agreed to be published under. */
	author: string;
	/** 1–5, as given. See `RATING_SCALE`. */
	rating: number;
	/** ISO 8601 date the review was written. */
	datePublished: string;
	/** The review itself, in the language it was written in. */
	body: string;
	/** BCP 47 tag for `body`, so a Thai review is not read as English. */
	inLanguage: string;
}

/** The scale `rating` is expressed on. Emitted with every rating in JSON-LD. */
export const RATING_SCALE = { worst: 1, best: 5 } as const;

/**
 * Reviews by strain directory.
 *
 * Empty until real ones exist. See the module comment before adding anything.
 */
export const REVIEWS: Record<string, Review[]> = {};

/** Every published review for a strain, newest first. */
export const reviewsFor = (strainId: string): Review[] =>
	[...(REVIEWS[strainId] ?? [])].sort((a, b) => b.datePublished.localeCompare(a.datePublished));

/**
 * Mean rating, rounded to one decimal, or `undefined` when there is nothing to
 * average.
 *
 * `undefined` rather than 0: an `aggregateRating` of zero is a *bad* rating, not
 * an absent one, and emitting it would be worse than emitting nothing.
 */
export function averageRating(reviews: Review[]): number | undefined {
	if (reviews.length === 0) return undefined;
	const total = reviews.reduce((sum, review) => sum + review.rating, 0);
	return Math.round((total / reviews.length) * 10) / 10;
}
